import { galerieALaction } from '../../auth/sujet.js';
import { Button, Checkbox, Dialog, Input, Switch, Textarea } from '@artosera/ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useAnnulerRendezVous,
  useCreerRendezVous,
  useModifierRendezVous,
  useRendezVous,
  type CorpsRendezVous,
  type RendezVousPresente,
} from '../../api/agenda.js';
import { useEquipe } from '../../api/equipe.js';
import { estApiError } from '../../api/errors.js';
import { useShellContext } from '../shell/context.js';
import { Field } from '../Field.js';
import { RechercheReference, type ReferenceAgenda } from './RechercheReference.js';
import { dateCivileParis, instantParisRegie, FUSEAU_AGENDA } from './temps.js';
import styles from './AgendaPage.module.css';
export function RendezVousDialog({
  open,
  onOpenChange,
  rendezVousId,
  jourInitial,
  heureInitiale,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  rendezVousId?: string;
  jourInitial?: string;
  /** `HH:MM` : le créneau choisi dans le calendrier ; le rendez-vous dure une heure. */
  heureInitiale?: string;
}) {
  const { galerieSlug, galerie } = useShellContext();
  const { t } = useTranslation(['regie', 'commun']);
  const fiche = useRendezVous(galerieSlug, open ? rendezVousId : undefined);
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={t(
        rendezVousId ? 'regie:agenda.rendezVous.modifier' : 'regie:agenda.rendezVous.nouveau',
      )}
      closeLabel={t('commun:actions.fermer')}
    >
      {open && fiche.data && !galerieALaction(galerie, 'agenda:write') ? (
        <div>
          <p>{fiche.data.rendezVous.titre}</p>
          <p>{fiche.data.rendezVous.lieu}</p>
          <p>{fiche.data.rendezVous.note}</p>
          <time>
            {new Intl.DateTimeFormat(undefined, {
              timeZone: 'Europe/Paris',
              dateStyle: 'medium',
              timeStyle: 'short',
            }).format(new Date(fiche.data.rendezVous.debut))}
          </time>
        </div>
      ) : open && (!rendezVousId || fiche.data) ? (
        <Formulaire
          key={rendezVousId ?? 'nouveau'}
          galerieSlug={galerieSlug}
          initial={fiche.data?.rendezVous}
          jourInitial={jourInitial}
          heureInitiale={heureInitiale}
          fermer={() => onOpenChange(false)}
        />
      ) : fiche.isError ? (
        <p role="alert">{t('commun:erreurReseau')}</p>
      ) : (
        <p>{t('commun:chargement')}</p>
      )}
    </Dialog>
  );
}
/** `09:00` → `10:00` ; reste dans la journée (`23:00` → `23:59`). */
function plusUneHeure(heure: string): string {
  const [h, m] = heure.split(':').map(Number);
  return (h ?? 0) >= 23
    ? '23:59'
    : `${String((h ?? 0) + 1).padStart(2, '0')}:${String(m ?? 0).padStart(2, '0')}`;
}
function Formulaire({
  galerieSlug,
  initial,
  jourInitial,
  heureInitiale,
  fermer,
}: {
  galerieSlug: string;
  initial?: RendezVousPresente;
  jourInitial?: string;
  heureInitiale?: string;
  fermer: () => void;
}) {
  const { t } = useTranslation(['regie', 'commun']);
  const creer = useCreerRendezVous(galerieSlug);
  const modifier = useModifierRendezVous(galerieSlug);
  const annuler = useAnnulerRendezVous(galerieSlug);
  const equipe = useEquipe(galerieSlug);
  const heure = (d: string) =>
    new Intl.DateTimeFormat('en-GB', {
      timeZone: FUSEAU_AGENDA,
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(d));
  const [titre, setTitre] = useState(initial?.titre ?? '');
  const [date, setDate] = useState(
    initial
      ? dateCivileParis(new Date(initial.debut))
      : (jourInitial ?? dateCivileParis(new Date())),
  );
  const [dateFin, setDateFin] = useState(
    initial ? dateCivileParis(new Date(initial.fin)) : (jourInitial ?? dateCivileParis(new Date())),
  );
  const [debut, setDebut] = useState(initial ? heure(initial.debut) : (heureInitiale ?? '10:00'));
  const [fin, setFin] = useState(
    initial ? heure(initial.fin) : heureInitiale ? plusUneHeure(heureInitiale) : '11:00',
  );
  const [journeeEntiere, setJourneeEntiere] = useState(initial?.journeeEntiere ?? false);
  const [lieu, setLieu] = useState(initial?.lieu ?? '');
  const [note, setNote] = useState(initial?.note ?? '');
  const [rappel, setRappel] = useState(initial?.rappel ?? true);
  const [participants, setParticipants] = useState(initial?.participants ?? []);
  const [references, setReferences] = useState<ReferenceAgenda[]>([
    ...(initial?.contactId ? [{ type: 'contact' as const, id: initial.contactId }] : []),
    ...(initial?.expositionId ? [{ type: 'exposition' as const, id: initial.expositionId }] : []),
  ]);
  const [erreur, setErreur] = useState<string | null>(null);
  const [confirmer, setConfirmer] = useState(false);
  async function soumettre() {
    setErreur(null);
    try {
      const lendemain = new Date(`${date}T12:00:00Z`);
      lendemain.setUTCDate(lendemain.getUTCDate() + 1);
      const corps: CorpsRendezVous = {
        titre,
        debut: instantParisRegie(date, journeeEntiere ? '00:00' : debut).toISOString(),
        fin: instantParisRegie(
          journeeEntiere ? lendemain.toISOString().slice(0, 10) : dateFin,
          journeeEntiere ? '00:00' : fin,
        ).toISOString(),
        journeeEntiere,
        lieu,
        note,
        rappel,
        participants,
        contactId: references.find((r) => r.type === 'contact')?.id ?? null,
        expositionId: references.find((r) => r.type === 'exposition')?.id ?? null,
        oeuvreIds: initial?.oeuvreIds ?? [],
      };
      if (initial) {
        if (
          date === dateCivileParis(new Date(initial.debut)) &&
          dateFin === dateCivileParis(new Date(initial.fin)) &&
          debut === heure(initial.debut) &&
          fin === heure(initial.fin) &&
          journeeEntiere === initial.journeeEntiere
        ) {
          corps.debut = initial.debut;
          corps.fin = initial.fin;
        }

        const changements = Object.fromEntries(
          Object.entries(corps).filter(
            ([k, v]) => JSON.stringify(v) !== JSON.stringify(initial[k as keyof CorpsRendezVous]),
          ),
        ) as Partial<CorpsRendezVous>;
        if (Object.keys(changements).length)
          await modifier.mutateAsync({ id: initial.id, corps: changements });
      } else await creer.mutateAsync(corps);
      fermer();
    } catch (e) {
      setErreur(estApiError(e) ? e.message : t('commun:erreurReseau'));
    }
  }
  const champ = (cle: string, type: string, value: string, changer: (v: string) => void) => (
    <Field htmlFor={`rdv-${cle}`} label={t(`regie:agenda.rendezVous.${cle}`)}>
      <Input
        id={`rdv-${cle}`}
        type={type}
        required
        value={value}
        onChange={(e) => changer(e.target.value)}
      />
    </Field>
  );
  return (
    <form
      className={styles.formulaire}
      onSubmit={(e) => {
        e.preventDefault();
        void soumettre();
      }}
    >
      {champ('titre', 'text', titre, setTitre)}
      {champ('date', 'date', date, (v) => {
        if (dateFin === date) setDateFin(v);
        setDate(v);
      })}
      <Switch
        label={t('regie:agenda.touteLaJournee')}
        checked={journeeEntiere}
        onCheckedChange={setJourneeEntiere}
      />
      {!journeeEntiere ? (
        <div className={styles.heures}>
          {champ('dateFin', 'date', dateFin, setDateFin)}
          {champ('debut', 'time', debut, setDebut)}
          {champ('fin', 'time', fin, setFin)}
        </div>
      ) : null}
      <Field htmlFor="rdv-lieu" label={t('regie:agenda.rendezVous.lieu')}>
        <Input id="rdv-lieu" value={lieu} onChange={(e) => setLieu(e.target.value)} />
      </Field>
      <RechercheReference
        galerieSlug={galerieSlug}
        types={['contact', 'exposition']}
        valeurs={references}
        onChanger={(v) =>
          setReferences(v.filter((r, i) => v.findLastIndex((a) => a.type === r.type) === i))
        }
      />
      <fieldset>
        <legend>{t('regie:agenda.rendezVous.participants')}</legend>
        {equipe.data?.membres
          .filter((m) => !m.support)
          .map((m) => (
            <Checkbox
              key={m.compteId}
              id={`participant-${m.compteId}`}
              label={m.nom}
              checked={participants.includes(m.compteId)}
              onCheckedChange={(v) =>
                setParticipants(
                  v
                    ? [...participants, m.compteId]
                    : participants.filter((id) => id !== m.compteId),
                )
              }
            />
          ))}
      </fieldset>
      <Field htmlFor="rdv-note" label={t('regie:agenda.rendezVous.note')}>
        <Textarea id="rdv-note" value={note} onChange={(e) => setNote(e.target.value)} />
      </Field>
      <Switch
        label={t('regie:agenda.rendezVous.rappel')}
        checked={rappel}
        onCheckedChange={setRappel}
      />
      {erreur ? <p role="alert">{erreur}</p> : null}
      <div className={styles.actions}>
        <Button
          type="submit"
          disabled={creer.isPending || modifier.isPending || Boolean(initial?.annuleLe)}
        >
          {t('regie:agenda.rendezVous.enregistrer')}
        </Button>
        {initial && !initial.annuleLe ? (
          <Button type="button" variant="ghost" onClick={() => setConfirmer(true)}>
            {t('regie:agenda.rendezVous.annuler')}
          </Button>
        ) : null}
      </div>
      <Dialog
        open={confirmer}
        onOpenChange={setConfirmer}
        title={t('regie:agenda.rendezVous.confirmation')}
        closeLabel={t('commun:actions.fermer')}
        footer={
          <Button
            disabled={annuler.isPending}
            onClick={() => {
              if (initial)
                void annuler
                  .mutateAsync(initial.id)
                  .then(fermer)
                  .catch((e: unknown) =>
                    setErreur(estApiError(e) ? e.message : t('commun:erreurReseau')),
                  );
            }}
          >
            {t('regie:agenda.rendezVous.confirmer')}
          </Button>
        }
      />
    </form>
  );
}
